// ChatGPT-style single-pill task composer for the dashboard. One tile:
//   [ + ]  Add a task…                         [🎤] [send]
// The "+" opens a popover (like the mobile input's +) with Priority + a
// category picker + image upload. Live suggestions appear above the bar as you
// type, pulled from the user's own task history (/api/task-autocomplete/).
//
// Replaces the legacy multi-row TaskInput inside the floating composer dock.

import React, { useEffect, useRef, useState } from 'react';
import { Plus, ArrowUp, Camera, Star, X } from 'lucide-react';
import VoiceInputButton from './VoiceInputButton';
import { fetchTaskAutocomplete } from '../services/taskService';
import { scanTaskImage } from '../services/visionService';
import { CATEGORIES, getCategoryColor } from '../constants/categories';
import './TaskComposer.css';

// Lightweight keyword → category detection so the composer can pre-pick a
// category as you type (e.g. "buy eggs" → Shopping). User can override.
const CATEGORY_KEYWORDS = {
  Shopping: ['buy', 'grocery', 'groceries', 'milk', 'eggs', 'bread', 'store', 'shop', 'purchase', 'order'],
  Travel: ['flight', 'hotel', 'trip', 'travel', 'vacation', 'airport', 'visit', 'book a'],
  Work: ['meeting', 'email', 'report', 'project', 'deadline', 'call with', 'presentation', 'standup'],
  Health: ['gym', 'workout', 'run', 'doctor', 'medicine', 'exercise', 'yoga', 'walk'],
  Money: ['pay', 'bill', 'rent', 'budget', 'invoice', 'transfer', 'bank', 'tax'],
  Personal: ['birthday', 'family', 'friend', 'gift', 'mom', 'dad'],
};
const detectCategory = (text) => {
  const t = (text || '').toLowerCase();
  for (const [cat, kws] of Object.entries(CATEGORY_KEYWORDS)) {
    if (kws.some((k) => t.includes(k))) return cat;
  }
  return null;
};

function TaskComposer({ selectedDate, onAddTask }) {
  const [input, setInput] = useState('');
  const [priority, setPriority] = useState(false);
  const [category, setCategory] = useState(null);
  const [catTouched, setCatTouched] = useState(false);
  const [qty, setQty] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const [scanning, setScanning] = useState(false);

  const fileRef = useRef(null);
  const wrapRef = useRef(null);
  // Set when the user picks a suggestion, so the next typeahead pass skips the
  // re-fetch (which would otherwise immediately reopen the dropdown).
  const skipSuggestRef = useRef(false);

  const dateStr = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
    : undefined;

  // live typeahead (debounced)
  useEffect(() => {
    if (skipSuggestRef.current) { skipSuggestRef.current = false; return undefined; }
    const q = input.trim();
    if (q.length < 2) { setSuggestions([]); return undefined; }
    const t = setTimeout(async () => {
      setSuggestions(await fetchTaskAutocomplete(q));
    }, 200);
    return () => clearTimeout(t);
  }, [input]);

  // auto-pick a category from the text until the user overrides it
  useEffect(() => {
    if (catTouched) return;
    setCategory(detectCategory(input));
  }, [input, catTouched]);

  // close popover / suggestions on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const submit = (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text) return;
    const opts = qty ? { quantity: Number(qty) } : undefined;
    onAddTask(text, dateStr, priority, category, opts);
    setInput('');
    setPriority(false);
    setCategory(null);
    setCatTouched(false);
    setQty('');
    setSuggestions([]);
    setMenuOpen(false);
  };

  const pickCategory = (name) => {
    setCategory((cur) => (cur === name ? null : name));
    setCatTouched(true);
  };

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setScanning(true);
    setMenuOpen(false);
    try {
      const res = await scanTaskImage(file);
      const items = res?.tasks || [];
      const first = items[0];
      const text = typeof first === 'string' ? first : first?.text || first?.task_text;
      if (text) setInput(text);
    } catch (_) {
      window.alert('Could not read that image.');
    } finally {
      setScanning(false);
    }
  };

  const showSuggest = focused && suggestions.length > 0;

  return (
    <div className="tc" ref={wrapRef}>
      {showSuggest && (
        <div className="tc-suggest">
          {suggestions.map((s) => (
            <button
              key={s}
              className="tc-suggest-item"
              onMouseDown={(e) => {
                // Fill the input with the suggestion instead of submitting — the
                // user reviews/edits it and sends manually via Enter or the send
                // button.
                e.preventDefault();
                skipSuggestRef.current = true;
                setInput(s);
                setSuggestions([]);
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {menuOpen && (
        <div className="tc-menu">
          <button
            className={`tc-menu-row${priority ? ' on' : ''}`}
            onClick={() => setPriority((p) => !p)}
          >
            <Star size={16} /> <span>Priority</span>
            <span className={`tc-switch${priority ? ' on' : ''}`}><span /></span>
          </button>

          <div className="tc-menu-label">Category</div>
          <div className="tc-cat-chips">
            {CATEGORIES.map((c) => (
              <button
                key={c.name}
                className={`tc-cat-chip${category === c.name ? ' active' : ''}`}
                style={category === c.name ? { color: getCategoryColor(c.name), borderColor: getCategoryColor(c.name) } : undefined}
                onClick={() => pickCategory(c.name)}
              >
                <span className="tc-cat-dot" style={{ background: getCategoryColor(c.name) }} />
                {c.name}
              </button>
            ))}
          </div>

          <button className="tc-menu-row" onClick={() => fileRef.current?.click()} disabled={scanning}>
            <Camera size={16} /> <span>{scanning ? 'Reading image…' : 'Upload image'}</span>
          </button>
        </div>
      )}

      {input.trim() && (
        <div className="tc-context">
          <div className="tc-context-cats">
            {CATEGORIES.map((c) => (
              <button
                key={c.name}
                className={`tc-cat-chip${category === c.name ? ' active' : ''}`}
                style={category === c.name ? { color: getCategoryColor(c.name), borderColor: getCategoryColor(c.name) } : undefined}
                onClick={() => pickCategory(c.name)}
              >
                <span className="tc-cat-dot" style={{ background: getCategoryColor(c.name) }} />
                {c.name}
              </button>
            ))}
          </div>
          <div className="tc-context-extras">
            <button className={`tc-ctx-btn${priority ? ' active' : ''}`} onClick={() => setPriority((p) => !p)}>
              <Star size={13} /> Priority
            </button>
            <label className="tc-qty">
              <span>Qty</span>
              <input type="number" min="1" value={qty} placeholder="—" onChange={(e) => setQty(e.target.value)} />
            </label>
          </div>
        </div>
      )}

      <div className="tc-bar">
        <button
          className={`tc-plus${menuOpen ? ' open' : ''}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="More options"
        >
          {menuOpen ? <X size={18} /> : <Plus size={18} />}
        </button>

        <input
          className="tc-input"
          value={input}
          placeholder="Add a task…"
          onFocus={() => setFocused(true)}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        />

        {category && (
          <span className="tc-cat-badge" style={{ color: getCategoryColor(category), borderColor: getCategoryColor(category) }}>
            {category}
          </span>
        )}
        {priority && <Star className="tc-priority-flag" size={15} />}

        <VoiceInputButton onResult={(t) => setInput((prev) => (prev ? `${prev} ` : '') + t)} />

        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onImage} />

        <button className="tc-send" onClick={() => submit()} disabled={!input.trim()} aria-label="Add task">
          <ArrowUp size={18} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

export default TaskComposer;
